import PropTypes from 'prop-types';
import { forwardRef, useCallback } from 'react';
import { useSortableRowHandle } from './row-handle-context';

/**
 * Wrap the drag affordance (e.g. menu icon). Must be used inside a DraggableTable row.
 * forwardRef so parent components like antd Tooltip can attach a ref to the activator.
 */
const DraggableRowHandle = forwardRef(
  ({ children, className }, forwardedRef) => {
    const handle = useSortableRowHandle();

    const setRef = useCallback(
      (node) => {
        if (handle?.setActivatorNodeRef) {
          handle.setActivatorNodeRef(node);
        }
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [handle, forwardedRef],
    );

    if (!handle) {
      return children;
    }

    const { listeners } = handle;

    return (
      <span
        ref={setRef}
        className={className}
        style={{ display: 'inline-flex', touchAction: 'none', cursor: 'grab' }}
        {...listeners}>
        {children}
      </span>
    );
  },
);

DraggableRowHandle.displayName = 'DraggableRowHandle';

DraggableRowHandle.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

DraggableRowHandle.defaultProps = {
  children: null,
  className: undefined,
};

export default DraggableRowHandle;
